import React, {
  ReactNode,
  TouchEvent,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import debounce from 'lodash.debounce';
import cn from 'classnames';
import useDidMount from '../../hooks/useDidMount';
import { getDirection, Point, Direction } from './utils';
import styles from './styles.module.scss';

class Bridge {
  subscription: (() => void) | null;
  constructor() {
    this.subscription = null;
  }

  update(fn: () => void) {
    this.subscription = fn;
  }
  broadcast() {
    this.subscription && this.subscription();
  }
  clear() {
    this.subscription = null;
  }
}

const bridge = new Bridge();

interface Props {
  children: ReactNode;
  useBodyScroll?: boolean;
  showRefreshTip?: boolean;
  onScroll?: (e: React.UIEvent) => void;
  onPullRefresh?: () => void;
  onReachEnd?: () => void;
  loading?: boolean;
  more?: boolean;
  noMoreText?: string;
  noMoreTextStyle?: string;
}

function ListContainer(props: Props) {
  const {
    children,
    useBodyScroll = true,
    showRefreshTip = true,
    onScroll,
    onReachEnd,
    onPullRefresh,
    more,
    loading,
    noMoreText,
    noMoreTextStyle,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const hideRef = useRef<HTMLDivElement>(null);
  const [startPoint, setStartPoint] = useState<Point>({ x: 0, y: 0 });
  const [originalScrollTop, setOriginalScrollTop] = useState(0);
  const maxDistance = 60;

  const container = useMemo(() => {
    if (typeof document !== 'undefined' && useBodyScroll) {
      return { current: document.documentElement || document.body };
    }

    return containerRef;
  }, [useBodyScroll]);

  useEffect(() => {
    if (onReachEnd) {
      bridge.update(() => {
        if (onReachEnd) {
          onReachEnd();
        }
      });
    }
  }, [onReachEnd]);

  useDidMount(function () {
    let scroll: any;

    if (useBodyScroll && onReachEnd) {
      scroll = debounce(function () {
        const scrollTop =
          document.documentElement.scrollTop || document.body.scrollTop;
        const isReachEnd =
          scrollTop + document.body.clientHeight + maxDistance >
          document.body.scrollHeight;

        if (isReachEnd) {
          bridge.broadcast();
        }
      }, 300);
      window.addEventListener('scroll', scroll);
    }

    return () => {
      if (useBodyScroll && onReachEnd) {
        window.removeEventListener('scroll', scroll);
        bridge.clear();
      }
    };
  });

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (containerRef.current) {
      containerRef.current.style.transition = '';
      setOriginalScrollTop(containerRef.current.scrollTop);
    }

    setStartPoint({
      x: e.touches[0].pageX,
      y: e.touches[0].pageY,
    });
  };

  const handleTouchEnd = () => {
    if (hideRef.current) {
      hideRef.current.style.transition = 'height 0.5s ease 1s';
      hideRef.current.style.height = '0px';
    }
  };

  const handlePullDown = (e: TouchEvent<HTMLDivElement>) => {
    const currentTouch = e.touches[0];
    const currentPoint = { x: currentTouch.pageX, y: currentTouch.pageY };

    if (hideRef.current) {
      const scrollDistance = currentPoint.y - startPoint.y;

      if (
        scrollDistance - originalScrollTop > 0 &&
        scrollDistance - originalScrollTop < maxDistance
      ) {
        if (onPullRefresh) {
          document.documentElement.classList.add('consult_overflowHidden');
          hideRef.current.style.height = `${scrollDistance}px`;
          hideRef.current.style.transition = 'none';

          onPullRefresh();
        }
      }
    }
  };

  const handlePullUp = debounce(() => {
    let isReachEnd = false;

    if (container.current) {
      const scrollTop = useBodyScroll
        ? document.documentElement.scrollTop || document.body.scrollTop
        : container.current.scrollTop;

      isReachEnd =
        scrollTop + container.current.clientHeight + maxDistance >=
        container.current.scrollHeight;

      if (isReachEnd && onReachEnd) {
        onReachEnd();
      }
    }
  }, 300);

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    const currentTouch = e.touches[0];
    const currentPoint = { x: currentTouch.pageX, y: currentTouch.pageY };

    const direction = getDirection(startPoint, currentPoint);

    if (direction === Direction.Down) {
      handlePullDown(e);
    } else if (direction === Direction.Up && !useBodyScroll) {
      handlePullUp();
    }
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onScroll={e => onScroll && onScroll(e)}
      ref={containerRef}
      className={cn(
        styles.container,
        !showRefreshTip ? styles.noMarginTop : null
      )}
    >
      {showRefreshTip ? (
        <div className={styles.indicator} ref={hideRef}>
          <span>下拉刷新</span>
        </div>
      ) : null}
      <div className={styles.content}>{children}</div>
      <div
        className={cn(styles.statusTxt, noMoreTextStyle, {
          [styles.loading]: loading,
        })}
      >
        {more !== undefined && !more
          ? noMoreText || '没有更多了...'
          : loading
          ? '加载中...'
          : ''}
      </div>
    </div>
  );
}

export default ListContainer;
